
/**
 * Module dependencies.
 */

import { Heading, Link, Text } from 'slyk-web-libs/design-system';
import { Icon } from 'react-components/media';
import { ProductItem } from 'client/components/core/products/product-item';
import { Product as ProductType } from 'slyk-web-libs/types';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { range } from 'lodash';
import { useBreakpoint } from 'slyk-web-libs/breakpoint';
import { useTranslate } from 'slyk-web-libs/i18n';
import noImage32Icon from 'client/assets/svg/32/no-image.svg';
import noImage72Icon from 'client/assets/svg/72/no-image.svg';
import payspaceRoutes from 'slyk-web-libs/config/payspace-routes';
import styled, { css } from 'styled-components';

/**
 * `ProductHighlights` type.
 */

type ProductHighlights = ProductType & {
  displayPrice: string;
  displayPriceConverted: string | null;
};

/**
 * `Props` type.
 */

type Props = {
  hasEarnRewards: boolean;
  products: Array<ProductHighlights>;
};

/**
 * Max highlight items.
 */

const maxHighlightItems = 4;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  ${media.min('ms')`
    border: 1px solid ${color('gray300')};
    border-radius: 12px;
    padding: 16px;
  `}
`;

/**
 * `HeaderWrapper` styled component.
 */

const HeaderWrapper = styled.div`
  align-items: center;
  display: grid;
  gap: 8px;
  grid-template-columns: 1fr max-content;
  margin-bottom: 32px;
`;

/**
 * `Title` styled component.
 */

const Title = styled(Heading).attrs({ as: 'h4', level: 'h4' })`
  color: ${color('primary')};
  font-weight: 600;
`;

/**
 * `ViewAll` styled component.
 */

const ViewAll = styled(Text).attrs({ as: 'span', size: 'smalll' })`
  font-weight: 700;
`;

/**
 * `Products` styled component.
 */

const Products = styled.ul<{ hasEarnRewards: boolean }>`
  display: grid;
  gap: 20px 8px;
  grid-template-columns: 1fr;

  ${media.min('xxs')`
    gap: 24px 16px;
    grid-template-columns: repeat(2, 1fr);
  `}

  ${media.min('ms')`
    grid-template-columns: repeat(3, 1fr);
  `}

  ${media.min('lg')`
    grid-template-columns: repeat(4, 1fr);

    ${ifProp('hasEarnRewards', css`
      grid-template-columns: repeat(2, 1fr);
      grid-template-rows: repeat(2, 1fr);
    `)}
  `}
`;

/**
 * `DefaultProductItem` styled component.
 */

const DefaultProductItem = styled.li<{ isVisible: boolean }>`
  display: none;

  &:last-child {
    ${ifProp('isVisible', css`
      display: block;
    `)}
  }

  ${media.min('xs')`
    &:last-child {
      display: none;
    }

    &:nth-child(-n+3) {
      display: block;
    }
  `}

  ${media.min('ms')`
    &:not(:nth-child(n+5)) {
      display: block;
    }
  `}
`;

/**
 * `DefaultProductImageWrapper` styled component.
 */

const DefaultProductImageWrapper = styled.div`
  align-items: center;
  aspect-ratio: 3 / 4;
  background-color: ${color('gray300')};
  border-radius: 6px;
  box-shadow: 0 1px 3px 0 rgba(25, 28, 30, 0.07);
  display: flex;
  justify-content: center;
  transition: background-color ${theme('animations.defaultTransition')};

  ${media.min('ms')`
    background-color: ${color('gray200')};
  `}
`;

/**
 * Product line.
 */

const productLine = css`
  background-color: ${color('gray300')};
  border-radius: 2px;
  height: 13px;
  transition: background-color ${theme('animations.defaultTransition')};

  ${media.min('ms')`
    background-color: ${color('gray200')};
  `}
`;

/**
 * `ProductNameLine` styled component.
 */

const ProductNameLine = styled.div`
  ${productLine}

  width: 100%;
`;

/**
 * `ProductAmountLine` styled component.
 */

const ProductAmountLine = styled.div`
  ${productLine}

  width: 35%;
`;

/**
 * `LinesWrapper` styled component.
 */

const LinesWrapper = styled.div`
  display: grid;
  padding-top: 10px;
  row-gap: 8px;

  ${media.min('lg')`
    padding-top: 20px;
  `}
`;

/**
 * Export `Highlights` component.
 */

export function Highlights(props: Props): JSX.Element {
  const { hasEarnRewards, products } = props;
  const { translate } = useTranslate();
  const productsLength = products.length;
  const defaultProducts = maxHighlightItems - productsLength % maxHighlightItems;
  const isDesktop = useBreakpoint('xl', 'min');

  return (
    <Wrapper>
      <HeaderWrapper>
        <Title>
          {translate('highlights.title')}
        </Title>

        <Link to={payspaceRoutes.products.base}>
          <ViewAll>
            {translate('common.labels.viewAll')}
          </ViewAll>
        </Link>
      </HeaderWrapper>

      <Products hasEarnRewards={hasEarnRewards}>
        {products.map(product => {
          return (
            <ProductItem
              key={product.id}
              product={product}
            />
          );
        })}

        {productsLength < maxHighlightItems && defaultProducts !== maxHighlightItems && range(defaultProducts)
          .map(index => (
            <DefaultProductItem
              isVisible={productsLength === 3 || productsLength === 1}
              key={index}
            >
              <DefaultProductImageWrapper>
                <Icon
                  aria-hidden
                  color={isDesktop ? color('gray500') : color('gray400')}
                  icon={isDesktop ? noImage72Icon : noImage32Icon}
                  size={isDesktop ? '72px' : '32px'}
                />
              </DefaultProductImageWrapper>

              <LinesWrapper>
                <ProductNameLine />

                <ProductAmountLine />
              </LinesWrapper>
            </DefaultProductItem>
          ))}
      </Products>
    </Wrapper>
  );
}
